import { gql } from 'apollo-boost';

export const FRAGMENT_BOOKING_DETAILS_BOOKING_AMENDMENT_DETAIL = gql`
  fragment fragmentBookingDetailsBookingAmendmentDetail on AmendmentDetail {
    checkedIn
    editInfo {
      createdAt
      createdBy
    }
    installmentName {
      target
    }
    pricePerNight {
      source
      target
    }
    sourceBookingId
    status
    targetBookingId
    tenancyEndDate {
      source
      target
    }
    totalPrice {
      source
      target
    }
    totalPriceOriginal {
      source
      target
    }
    type
  }
`;

export const FRAGMENT_BOOKING_DETAILS_BOOKING_ACTIVE_BOOKING_ON_SAME_CHAIN = gql`
  fragment fragmentBookingDetailsBookingActiveBookingOnSameChain on Booking {
    id
    amendmentDetails {
      ...fragmentBookingDetailsBookingAmendmentDetail
    }
    latestAmendmentDetail {
      ...fragmentBookingDetailsBookingAmendmentDetail
    }
    property {
      id
    }
    status
    tenancyOption {
      name
      startDate
      endDate
      length
    }
    tenancyLengthOption
  }
  ${FRAGMENT_BOOKING_DETAILS_BOOKING_AMENDMENT_DETAIL}
`;

export const FRAGMENT_BOOKING_DETAILS_BOOKING_ACADEMIC_YEAR = gql`
  fragment fragmentBookingDetailsBookingAcademicYear on AcademicYear {
    id
    name
    fromYear
    toYear
    flexibilityControlInfo {
      ageRestrictionForBooking
      setUpRateType
    }
    hasService
  }
`;

export const FRAGMENT_BOOKING_DETAILS_BOOKING_BALANCE_INFO = gql`
  fragment fragmentBookingDetailsBookingBalanceInfo on BalanceInfo {
    amendmentCreditBalance
    bookingBalance
    currentBalance
    financialStatus
    processingBalance
    refundableBalance
    totalBalance
    walletBalance
    refundingWalletAmountOffline
    refundingWalletAmount
    refundingAmendmentCreditAmountOffline
    refundingAmendmentCreditAmount
  }
`;

export const FRAGMENT_BOOKING_DETAILS_BOOKING_ROOM_TYPE = gql`
  fragment fragmentBookingDetailsBookingRoomType on BookingRoomTypeInfo {
    name
    roomTypeId
  }
`;

export const FRAGMENT_BOOKING_DETAILS_BOOKING_FORMS = gql`
  fragment fragmentBookingDetailsBookingForms on BookingForm {
    editInfo {
      updatedAt
      updatedBy
    }
    filePath
    id
    name
    status
    type
  }
`;

export const FRAGMENT_BOOKING_DETAILS_BOOKING_PAYMENT_ITEM = gql`
  fragment fragmentBookingDetailsBookingPaymentItem on PaymentItem {
    name
    description
    dueDatetime
    category
    chargeCreated
    currency
    id
    invoiceDatetime
    isDueBeforeBookingConfirmed
    isPartOfRent
    isRefundable
    modelId
    modelType
    originId
    paidAmount
    status
    taxAmount
    taxName
    taxRate
    totalAmount
    paidAmountFromCredit
    paidAmountFromWallet
    paidAmountFromAmendmentCredit
    editInfo {
      createdAt
    }
    transactionRecords {
      id
      type
      editInfo {
        createdAt
      }
      amount
    }
    refundableTransactionRecords {
      bookingChainId
      partnerId
      propertyId
      paymentItemId
      description
      referenceNumber
      transactionNumber
      actualTransactionDate
      amountReturnedToStudent
      type
      id
      referenceInfo
      sourceRefundableTransactionRecord {
        id
        partnerId
        description
        referenceNumber
        transactionNumber
        actualTransactionDate
        amountReturnedToStudent
        type
        partner {
          id
          name
        }
      }
      partner {
        id
        name
      }
    }
    paymentItemComponents {
      amount
      isTaxIncludedInPrice
      serviceName
      taxAmount
      taxName
      taxRate
    }
  }
`;

export const FRAGMENT_BOOKING_DETAILS_BOOKING_ROOM_ALLOCATION = gql`
  fragment fragmentBookingDetailsBookingRoomAllocation on RoomAllocationInfo {
    bedId
    bedName
    buildingName
    floorName
    flatName
    roomName
  }
`;

export const FRAGMENT_STAFF_NOTE = gql`
  fragment FragmentStaffNote on StaffNote {
    id
    type
    category
    content
    status
    visibleOnStudentLevel
    resolvedAt
    resolvedBy
    reopenedBy
    reopenedAt
    originBookingChainId
    editInfo {
      createdAt
      createdBy
      updatedAt
      updatedBy
    }
  }
`;

export const FRAGMENT_BOOKING_DETAILS_BOOKING_STUDENT_DETAILS_BOOKING = gql`
  fragment fragmentBookingDetailsBookingStudentDetailsBooking on BaseBooking {
    id
    originId
    status
    bookingChainId
    category
    heldToTime
    bookingTips {
      type
      createdAt
    }
    countDown
    roomAllocation {
      bedName
      buildingName
      floorName
      flatName
      roomName
    }
    tenancyOption {
      name
      startDate
      endDate
      length
    }
    balanceInfo {
      totalBalance
    }
    academicYear {
      id
      name
    }
    roomType {
      name
    }
    property {
      id
    }
    staffNotes {
      ...FragmentStaffNote
    }
    editInfo {
      updatedAt
    }
    bedStatus {
      id
      bed {
        id
        name
        building {
          id
          name
        }
      }
    }
  }
  ${FRAGMENT_STAFF_NOTE}
`;

export const FRAGMENT_BOOKING_DETAILS_BOOKING_STUDENT = gql`
  fragment fragmentBookingDetailsBookingStudent on Student {
    id
    originId
    firstName
    middleName
    lastName
    fullName
    gender
    profilePhotoFileUuid
    account {
      id
      email
      emailVerified
    }
    generalInfo {
      basic {
        dateOfBirth
        nationality
      }
      contact {
        postcode
        mobilePhoneAreaCode
        mobilePhoneNumber
      }
      education {
        destinationUniversity
      }
    }
    contact {
      emergencyContacts {
        id
        firstName
        middleName
        lastName
        title
        dateOfBirth
        countryTerritoryId
        countryTerritory
        cityTownId
        cityTown
        relationship
        addressLine1
        addressLine2
        postcode
        mobilePhoneAreaCode
        mobilePhoneNumber
        contactEmail
        contactWithoutPermission
      }
    }
    preferredLanguageFull
  }
`;

export const FRAGMENT_BOOKING_DETAILS_BOOKING_INSPECTION_REPORT = gql`
  fragment fragmentBookingDetailsBookingInspectionReport on InspectionReport {
    editable
    editInfo {
      updatedBy
    }
    id
    inspectionTime
    inspectionReportItems {
      facilityId
      itemName
      itemCategory
      status
      charge
      proofPhotoUuids
      comment
      paymentItem {
        taxRate
      }
    }
    name
  }
`;

export const FRAGMENT_BOOKING_DETAILS_BOOKING_TENANCY_OPTION = gql`
  fragment fragmentBookingDetailsBookingTenancyOption on BookingTenancyOptionInfo {
    tenancyOptionId
    name
    startDate
    endDate
    length
    intervalForTurnaround
  }
`;

const FRAGMENT_BOOKING_PROPERTY = gql`
  fragment FragmentProperty on Property {
    additionalCurrencies {
      id
      originId
      currencyCode
      exchangeRate
    }
    billingCycle
    currency
    id
    name
    originId
    timezone
  }
`;

export const FRAGMENT_OUT_OF_ROOM_RECORDS = gql`
  fragment fragmentOutOfRoomRecords on OutOfRoomRecord {
    id
    reason
    awayFrom
    awayTo
  }
`;

export const FRAGMENT_GUEST = gql`
  fragment fragmentGuest on Guest {
    id
    fullName
    relationship
    stayEndDate
    stayStartDate
    dateOfBirth
    contactEmail
    mobilePhoneAreaCode
    mobilePhoneNumber
    guestType
    fileUuids
    firstName
    middleName
    lastName
    issuedKey
  }
`;

export const FRAGMENT_BOOKING_DETAILS_BOOKING_DOCUMENT = gql`
  fragment fragmentBookingDetailsBookingDocument on Document {
    id
    name
    description
    mandatory
    category
    status
    fileUuid
    uploadedAt
    uploadedBy
    confirmedAt
    confirmedBy
    rejectedAt
    rejectedBy
    rejectedReason
    stage
    guarantorId
    mandatory
  }
`;

export const FRAGMENT_BOOKING_DETAILS_BOOKING_INSTALLMENT_PLAN = gql`
  fragment fragmentBookingDetailsBookingInstallmentPlan on BookingInstallmentPlan {
    amountDue
    dueDate
    id
    isDueBeforeBookingConfirmed
    name
    partOfRentAdditionalFeeDeduct
    partOfRentAdditionalFees {
      name
      amount
    }
    paymentItem {
      category
      description
      id
      modelType
      paidAmount
      paidAmountFromCredit
      paidAmountFromWallet
      status
      totalAmount
    }
  }
`;

export const FRAGMENT_BOOKING_NOMINATION_PLAN = gql`
  fragment fragmentBookingNominationPlan on NominationPlan {
    academicYear {
      id
    }
    bookingType
    endDate
    id
    isEnabled
    name
    startDate
    partner {
      id
      name
    }
    salesPlanTemplate {
      id
      studentPayTo
      isEnableHoldBed
      timeLengthToHoldTheBed
      invoiceCalculationType
    }
  }
`;

export const FRAGMENT_TAKEOVER_BOOKING = gql`
  fragment fragmentTakeoverBooking on Booking {
    id
    originId
    chargingStartDate
    chargingEndDate
    tenancyOption {
      ...fragmentBookingDetailsBookingTenancyOption
    }
    roomType {
      roomTypeId
      name
    }
    roomAllocation {
      ...fragmentBookingDetailsBookingRoomAllocation
    }
    serviceSelections {
      id
      serviceId
      serviceName
      serviceOptionName
      serviceOptionId
      serviceType
      serviceOriginalPriceContribution
      serviceIncrementType
      incrementPerBillingCycle
      incrementPerNight
      incrementInTotal
    }
    totalPrice
    totalPriceOriginal
    totalPriceAfterService
    installment {
      installmentId
      installmentType
      name
    }
    currency
    installmentPlans {
      ...fragmentBookingDetailsBookingInstallmentPlan
    }
    bookingAdditionalFees {
      additionalFeeCardConfigId
      dueAmountWithTax
      dueDate
      feature
      installmentIndex
      name
      paymentItem {
        id
        status
        paidAmount
      }
      dueAmount
      description
      installmentNumber
      taxRateId
      taxRate
      salesItemId
      taxAmount
      dueAmountWithTax
      isDueBeforeBookingConfirmed
    }
    checkOutCartInfo {
      paymentDueNow {
        amountDue
      }
    }
    student {
      ...fragmentBookingDetailsBookingStudent
    }
    property {
      id
    }
    paymentItems {
      id
      name
      paidAmountFromWallet
      paidAmountFromAmendmentCredit
    }
  }
  ${FRAGMENT_BOOKING_DETAILS_BOOKING_TENANCY_OPTION}
  ${FRAGMENT_BOOKING_DETAILS_BOOKING_ROOM_ALLOCATION}
  ${FRAGMENT_BOOKING_DETAILS_BOOKING_INSTALLMENT_PLAN}
  ${FRAGMENT_BOOKING_DETAILS_BOOKING_STUDENT}
`;

export const FRAGMENT_BOOKING_LINK = gql`
  fragment fragmentBookingLink on BookingLink {
    originalBooking {
      ...fragmentTakeoverBooking
    }
    replacementBooking {
      ...fragmentTakeoverBooking
    }
    occupiedBooking {
      ...fragmentTakeoverBooking
    }
    countDown
    type
    status
    sourceAcceptedAt
    sourceRejectedAt
    targetAcceptedAt
    targetRejectedAt
    editInfo {
      createdAt
      createdBy
      updatedAt
      updatedBy
    }
  }
  ${FRAGMENT_TAKEOVER_BOOKING}
`;

export const FRAGMENT_BOOKING = gql`
  fragment fragmentBookingDetailsBooking on Booking {
    isDifferentChargingDate
    isNeedManualReview
    chargingEndDate
    chargingStartDate
    tenancyOptionRateType
    isRolledOver
    isRolling
    isEffective
    rolloverReminderDate
    currentRollingRateValue
    curationTicket {
      id
      status
    }
    guests {
      id
      stayStartDate
      stayEndDate
    }
    rolloverBookings {
      id
      property {
        id
      }
      status
      tenancyOption {
        name
        startDate
        endDate
        length
      }
      tenancyLengthOption
    }
    sourceRollingBooking {
      id
      property {
        id
      }
      status
      tenancyOption {
        name
        startDate
        endDate
        length
      }
      tenancyLengthOption
    }
    bookingTips {
      type
      createdAt
    }
    outOfRoomRecords {
      ...fragmentOutOfRoomRecords
    }
    vouchers {
      id
      issued
      issuedDate
      value
      name
      description
      editInfo {
        createdAt
        createdBy
        updatedAt
        updatedBy
      }
      partner {
        name
        id
      }
    }
    bookingLink {
      type
      status
      occupiedBooking {
        id
        originId
      }
    }
    academicYear {
      ...fragmentBookingDetailsBookingAcademicYear
    }
    activeBookingsOnSameChain {
      ...fragmentBookingDetailsBookingActiveBookingOnSameChain
    }
    amendmentDetails {
      ...fragmentBookingDetailsBookingAmendmentDetail
    }
    arrivalInformation {
      id
      arrivalDate
      timeSlotFrom
      timeSlotTo
      modeOfArrival
      flightNumber
      message
      editInfo {
        createdAt
        createdBy
        updatedAt
        updatedBy
      }
    }
    attachments {
      id
      name
      fileUuid
      staffOnly
      editInfo {
        updatedAt
        updatedBy
      }
    }
    backupCard {
      id
    }
    balanceInfo {
      ...fragmentBookingDetailsBookingBalanceInfo
    }
    bedStatus {
      id
      status
      bed {
        id
      }
    }
    bookingAdditionalFees {
      additionalFeeCardConfigId
      dueAmountWithTax
      dueDate
      feature
      installmentIndex
      name
      paymentItem {
        id
        status
        paidAmount
      }
      dueAmount
      description
      installmentNumber
      taxRateId
      taxRate
      salesItemId
      taxAmount
      dueAmountWithTax
      isDueBeforeBookingConfirmed
    }
    bookingChainId
    bookingForms {
      ...fragmentBookingDetailsBookingForms
    }
    reasonNotes
    cancelledReason
    cancelledBy
    category
    checkOutCartInfo {
      paymentDueNow {
        amountDueOriginal
      }
      priceBreakDown {
        totalPrice
        details {
          name
          amount
          description
          discountName
          discountValue
          taxInfo {
            name
            taxRate
            amount
            includedInPrice
          }
        }
      }
    }
    checkInDate
    checkOutDate
    countDown
    contracts {
      id
      name
      status
      fileUuid
      contractFileStatus
    }
    creditBalanceInfo {
      totalValidAmount
      creditBalances {
        validAmount
        usedAmount
        remainingAmount
        category
        clawbackAmount
      }
    }
    currency
    customFieldValues {
      id
      labelName
      value
      customFieldId
      customFieldType
      customFieldMandatoryOnBookingJourney
      customFieldInputOnBookingJourney
      originId
      editInfo {
        createdAt
        createdBy
        updatedAt
        updatedBy
      }
    }
    defaultCard {
      id
      type
      authorizedAt
    }
    documents {
      ...fragmentBookingDetailsBookingDocument
    }
    bookingSpecialOffers {
      id
      discountAmount
      specialOfferId
      channelId
      channelType
      specialOfferName
      specialOfferType
      specialOfferDiscountType
      specialOfferDiscountValue
      specialOfferDiscountRule
      discountItemType
    }
    editable
    editInfo {
      createdAt
      createdBy
      updatedAt
      updatedBy
    }
    expiredBy
    expiredReason
    externalId
    guarantors {
      id
      firstName
      middleName
      lastName
      title
      dateOfBirth
      relationship
      contactEmail
      mobilePhoneAreaCode
      mobilePhoneNumber
      homePhoneAreaCode
      homePhoneNumber
      countryTerritory
      countryTerritoryId
      cityTown
      cityTownId
      addressLine1
      addressLine2
      postcode
      guarantorReferenceNumber
      taxCountryTerritory
      guarantorSettingType
      guarantorSettingTypeLabelName
      customFieldValues {
        id
        labelName
        value
      }
    }
    heldBy
    heldToTime
    id
    ignoreAgeRestriction
    installment {
      installmentId
      installmentType
      name
    }
    installmentPlanInfo {
      paidNumber
      totalNumber
      nextDueDate
      isArrears
      installmentPlanPaymentInfoes {
        status
        amount
        dueDate
        name
        partOfRentFeeInfoes {
          name
          amount
        }
      }
    }
    installmentPlans {
      ...fragmentBookingDetailsBookingInstallmentPlan
    }
    invoicedPaymentItems {
      id
      description
      status
      dueDatetime
      totalAmount
      paidAmount
    }
    isBedChangeableInSp
    isRebooker
    latestAmendmentDetail {
      ...fragmentBookingDetailsBookingAmendmentDetail
    }
    nominationPlan {
      ...fragmentBookingNominationPlan
    }

    offerStatus
    originId
    partnerReferenceId
    paymentItems {
      ...fragmentBookingDetailsBookingPaymentItem
    }
    priceForBillingCycle
    pricePerMonthAfterService
    pricePerMonthOriginal
    pricePerMonth
    pricePerNight
    pricePerNightOriginal
    property {
      ...FragmentProperty
    }
    rateFlexibility {
      rateFlexibilityType
      freeCancellationDeadlineDate
    }
    referral
    rentalTaxRate
    rentalTaxAmount
    rentalTaxRateType
    roomAllocation {
      ...fragmentBookingDetailsBookingRoomAllocation
    }
    roomType {
      ...fragmentBookingDetailsBookingRoomType
    }
    source
    specialRateOffered
    staffNotes {
      ...FragmentStaffNote
    }
    setBedChangeableInSpAt
    setBedChangeableInSpBy
    status
    statusBeforeDisabled
    statusLastChangedTime
    statusTime {
      termsAccepted
      cancelled
      expired
    }
    serviceSelections {
      id
      serviceId
      serviceName
      serviceOptionName
      serviceOptionId
      serviceType
      serviceOriginalPriceContribution
      serviceIncrementType
      incrementPerBillingCycle
      incrementPerNight
      incrementInTotal
    }
    pricePerNightAfterService
    totalPriceAfterService
    student {
      ...fragmentBookingDetailsBookingStudent
      bookings: baseBookings(propertyId: $propertyId) {
        ...fragmentBookingDetailsBookingStudentDetailsBooking
      }
    }
    tenancyOption {
      ...fragmentBookingDetailsBookingTenancyOption
    }
    tenancyOptionRateType
    tenancyLengthOption
    rollingRateValue
    terminatedReason
    totalPaidAmountFromWallet
    totalAmountFromAmendmentCredit
    totalPrice
    totalPriceAfterTax
    totalPriceOriginal
  }
  ${FRAGMENT_BOOKING_DETAILS_BOOKING_ACADEMIC_YEAR}
  ${FRAGMENT_BOOKING_DETAILS_BOOKING_ACTIVE_BOOKING_ON_SAME_CHAIN}
  ${FRAGMENT_BOOKING_DETAILS_BOOKING_AMENDMENT_DETAIL}
  ${FRAGMENT_BOOKING_DETAILS_BOOKING_BALANCE_INFO}
  ${FRAGMENT_BOOKING_DETAILS_BOOKING_FORMS}
  ${FRAGMENT_BOOKING_DETAILS_BOOKING_INSTALLMENT_PLAN}
  ${FRAGMENT_BOOKING_DETAILS_BOOKING_PAYMENT_ITEM}
  ${FRAGMENT_BOOKING_DETAILS_BOOKING_ROOM_ALLOCATION}
  ${FRAGMENT_BOOKING_DETAILS_BOOKING_ROOM_TYPE}
  ${FRAGMENT_BOOKING_DETAILS_BOOKING_STUDENT_DETAILS_BOOKING}
  ${FRAGMENT_BOOKING_DETAILS_BOOKING_STUDENT}
  ${FRAGMENT_BOOKING_DETAILS_BOOKING_TENANCY_OPTION}
  ${FRAGMENT_BOOKING_NOMINATION_PLAN}
  ${FRAGMENT_BOOKING_PROPERTY}
  ${FRAGMENT_STAFF_NOTE}
  ${FRAGMENT_OUT_OF_ROOM_RECORDS}
  ${FRAGMENT_BOOKING_DETAILS_BOOKING_DOCUMENT}
`;

export const QUERY_BOOKING = gql`
  query QueryBooking($propertyId: ID!, $id: ID!) {
    getBooking(propertyId: $propertyId, id: $id) {
      ...fragmentBookingDetailsBooking
    }
  }
  ${FRAGMENT_BOOKING}
`;

export const QUERY_BOOKING_LINKS = gql`
  query QueryBooking($propertyId: ID!, $id: ID!) {
    getBooking(propertyId: $propertyId, id: $id) {
      bookingLink {
        ...fragmentBookingLink
      }
    }
  }
  ${FRAGMENT_BOOKING_LINK}
`;

export const QUERY_VALID_TAXES = gql`
  query QueryValidTax($propertyId: ID!) {
    getValidTaxes(propertyId: $propertyId) {
      taxes {
        id
        name
        scopeLevel
        scopeCountry {
          id
          name
        }
        scopeCity {
          id
          name
        }
        taxRates {
          id
          taxRate
          validFrom
        }
        validTaxRate {
          id
          taxRate
        }
      }
    }
  }
`;

export const QUERY_RENT_TAX = gql`
  query GetRentTax($filter: GetRentTaxFilterInput!) {
    getRentTax(filter: $filter) {
      taxRate {
        id
        taxRate
      }
      taxIncludedInPrice
      targetTaxInfo {
        id
        taxInfo {
          id
          name
        }
      }
    }
  }
`;

export const MUTATION_CREATE_STAFF_NOTE = gql`
  mutation MutationCreateStaffNote($propertyId: ID!, $input: createStaffNoteInput!) {
    createStaffNote(propertyId: $propertyId, input: $input) {
      ...FragmentStaffNote
    }
  }
  ${FRAGMENT_STAFF_NOTE}
`;

export const MUTATION_UPDATE_STAFF_NOTE = gql`
  mutation MutationUpdateStaffNote($propertyId: ID!, $input: updateStaffNoteInput!) {
    updateStaffNote(propertyId: $propertyId, input: $input) {
      ...FragmentStaffNote
    }
  }
  ${FRAGMENT_STAFF_NOTE}
`;

export const MUTATION_DELETE_STAFF_NOTE = gql`
  mutation MutationDeleteStaffNote($propertyId: ID!, $staffNoteId: ID!) {
    deleteStaffNote(propertyId: $propertyId, staffNoteId: $staffNoteId) {
      success
    }
  }
`;

export const MUTATION_OFFER_BOOKING = gql`
  mutation OfferBooking($propertyId: ID!, $bookingId: ID!, $isBedChangeableInSp: Boolean) {
    offerBooking(
      propertyId: $propertyId
      bookingId: $bookingId
      isBedChangeableInSp: $isBedChangeableInSp
    ) {
      success
    }
  }
`;

export const MUTATION_APPLY_CREDIT_BALANCE = gql`
  mutation ApplyCreditBalance($propertyId: ID!, $input: applyCreditBalanceInput!) {
    applyCreditBalance(propertyId: $propertyId, input: $input) {
      id
    }
  }
`;

export const MUTATION_DELETE_TURNAROUND = gql`
  mutation MutationDeleteTurnaround($propertyId: ID!, $bookingId: ID!) {
    deleteTurnaround(propertyId: $propertyId, bookingId: $bookingId) {
      ...fragmentBookingDetailsBooking
    }
  }
  ${FRAGMENT_BOOKING}
`;

// query voucher type
export const QUERY_VOUCHER_TYPE = gql`
  query getPartners($propertyId: ID!, $filter: getPartnersFilterInput) {
    getPartners(propertyId: $propertyId, filter: $filter) {
      partners {
        id
        name
        editInfo {
          createdAt
          updatedAt
        }
      }
    }
  }
`;

// query vouchers
export const QUERY_BOOKING_VOUCHERS = gql`
  query QueryBooking($propertyId: ID!, $id: ID!) {
    getBooking(propertyId: $propertyId, id: $id) {
      vouchers {
        id
        issued
        issuedDate
        value
        name
        description
        editInfo {
          createdAt
          createdBy
          updatedAt
          updatedBy
        }
        partner {
          name
          id
        }
      }
    }
  }
`;

// query paymentItems in bookings and student
export const QUERY_PAYMENT_ITEMS_IN_BOOKINGS_AND_STUDENT = gql`
  query QueryBooking($propertyId: ID!, $id: ID!) {
    getBooking(propertyId: $propertyId, id: $id) {
      paymentItems {
        ...fragmentBookingDetailsBookingPaymentItem
      }
    }
  }
  ${FRAGMENT_BOOKING_DETAILS_BOOKING_PAYMENT_ITEM}
`;

export const QUERY_GET_CREDIT_TRANSACTION_RECORDS = gql`
  query getCreditTransactionRecords(
    $propertyId: ID!
    $filter: getCreditTransactionRecordsFilterInput!
  ) {
    getCreditTransactionRecords(propertyId: $propertyId, filter: $filter) {
      creditTransactionRecords {
        id
        validAmount
        usedAmount
        remainingAmount
        clawbackAmount
        type
        amount
        currency
        bookingId
        category
        description
        bookingChainId
        originBookingId
        isReversed
        editInfo {
          createdAt
          createdBy
        }
      }
    }
  }
`;

export const MUTATION_CRTERE_OUT_OF_ROOM_RECORD = gql`
  mutation MutationCreateOutOfRoomRecord($propertyId: ID!, $input: createOutOfRoomRecordInput!) {
    createOutOfRoomRecord(propertyId: $propertyId, input: $input) {
      ...fragmentOutOfRoomRecords
    }
  }
  ${FRAGMENT_OUT_OF_ROOM_RECORDS}
`;

export const QUERY_BOOKING_OUT_OF_ROOM_RECORDS = gql`
  query QueryBooking($propertyId: ID!, $id: ID!) {
    getBooking(propertyId: $propertyId, id: $id) {
      outOfRoomRecords {
        ...fragmentOutOfRoomRecords
      }
    }
  }
  ${FRAGMENT_OUT_OF_ROOM_RECORDS}
`;

export const MUTATION_UPDATE_OUT_OF_ROOM_RECORD = gql`
  mutation MutationUpdateOutOfRoomRecord($propertyId: ID!, $input: updateOutOfRoomRecordInput!) {
    updateOutOfRoomRecord(propertyId: $propertyId, input: $input) {
      ...fragmentOutOfRoomRecords
    }
  }
  ${FRAGMENT_OUT_OF_ROOM_RECORDS}
`;

export const QUERY_GET_GUESTS_BY_BOOKING_ID = gql`
  query GetGuestsByBookingId($propertyId: ID!, $bookingId: ID!) {
    getGuestsByBookingId(propertyId: $propertyId, bookingId: $bookingId) {
      ...fragmentGuest
    }
  }
  ${FRAGMENT_GUEST}
`;

export const MUTATION_CREATE_GUEST = gql`
  mutation CreateGuest($propertyId: ID!, $input: createGuestInput!) {
    createGuest(propertyId: $propertyId, input: $input) {
      ...fragmentGuest
    }
  }
  ${FRAGMENT_GUEST}
`;

export const MUTATION_UPDATE_GUEST = gql`
  mutation UpdateGuest($propertyId: ID!, $input: updateGuestInput!) {
    updateGuest(propertyId: $propertyId, input: $input) {
      ...fragmentGuest
    }
  }
  ${FRAGMENT_GUEST}
`;

export const MUTATION_DELETE_GUEST = gql`
  mutation DeleteGuest($propertyId: ID!, $input: deleteGuestInput!) {
    deleteGuest(propertyId: $propertyId, input: $input) {
      ...fragmentGuest
    }
  }
  ${FRAGMENT_GUEST}
`;

export const MUTATION_DELETE_OUT_OF_ROOM_RECORD = gql`
  mutation MutationDeleteOutOfRoomRecord($propertyId: ID!, $id: ID!) {
    deleteOutOfRoomRecord(propertyId: $propertyId, id: $id) {
      success
    }
  }
`;

export const QUERY_GET_PAYMENT_METHOD_LINK_RECORDS = gql`
  query getPaymentMethodLinkRecords($propertyId: ID!, $bookingId: ID!) {
    getPaymentMethodLinkRecords(propertyId: $propertyId, bookingId: $bookingId) {
      records {
        id
        bookingId
        paymentMethodType
        recordType
        paymentMethodId
        cardPriority
        editInfo {
          createdAt
        }
      }
    }
  }
`;

export const MUTATION_REVERSE_RESIDENT_TO_BOOKING_CONFIRM_OR_COMPLATED = gql`
  mutation MutationReverseResidentToBookingConfirmedOrCompleted($propertyId: ID!, $bookingId: ID!) {
    reverseResidentToBookingConfirmedOrCompleted(propertyId: $propertyId, bookingId: $bookingId) {
      id
    }
  }
`;

export const MUTATION_RESET_BOOKING_DOCUMENT_STATUS = gql`
  mutation MutationResetBookingDocumentStatus(
    $propertyId: ID!
    $input: resetBookingDocumentStatusInput!
  ) {
    resetBookingDocumentStatus(propertyId: $propertyId, input: $input) {
      id
    }
  }
`;

export const QUERY_FLAT_PREFERENCE_PLAN_SETTING = gql`
  query QueryFlatPreferencePlanSetting($id: ID!) {
    node(id: $id) {
      ... on Property {
        id
        flatPreferencePlanSetting {
          smokingEnabled
          petEnabled
          dietaryEnabled
          genderEnabled
        }
      }
    }
  }
`;

export const QUERY_BOOKING_CHECKIN_TASK_STARTED = gql`
  query QueryBooking($propertyId: ID!, $id: ID!) {
    getBooking(propertyId: $propertyId, id: $id) {
      id
      checkInTaskStarted
    }
  }
`;

export const QUERY_BOOKING_INSPECTION_REPORTS = gql`
  query QueryBookingInspectionReports($propertyId: ID!, $id: ID!) {
    getBooking(propertyId: $propertyId, id: $id) {
      id
      inspectionReports {
        ...fragmentBookingDetailsBookingInspectionReport
      }
    }
  }
  ${FRAGMENT_BOOKING_DETAILS_BOOKING_INSPECTION_REPORT}
`;

export const QUERY_GET_IS_BED_AVAILABLE_FOR_ROLLING_FROM_BOOKING = gql`
  query GetIsBedAvailableForRollingFromBooking($propertyId: ID!, $getBookingId: ID!) {
    getBooking(propertyId: $propertyId, id: $getBookingId) {
      id
      isBedAvailableForRolling
    }
  }
`;
